import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import {
  HomePage,
  ContactUsPage,
  FAQPage,
  AboutUsPage,
} from "pages/LandingPage";
import {
  DashboardPage,
  ProfileSettingsPage,
  SupportHelpPage,
  TransactionsPage,
  WalletPage,
} from "pages/Dashboard";
import {
  LoginPage,
  SignupPage,
  ForgetPassword,
  TwoFactorAuthentication,
  VerifyEmailPage,
  VerificationCodePage,
} from "pages/Auth";
import { AuthLayout, DashboardLayout, HomeLayout } from "layouts";
import NotFoundPage from "pages/commons/NotFoundPage";
import PrivacyPolicyPage from "pages/LandingPage/PrivacyPolicyPage/PrivacyPolicyPage";
import TermsConditionPage from "pages/LandingPage/TermsConditionPage/TermsConditionPage";
import TransactionDetails from "pages/Dashboard/TransactionsDetails/TransactionDetails";
import AdminLayout from "layouts/admin/_layout";
import {
  AdminDashboardPage,
  AdminLoginPage,
  AdminTransactionPage,
  AdminUserPage,
  AdminWalletPage,
} from "pages/Admin";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { getFromStore, StoreType } from "services/localStorage";
import { AuthData } from "reduxStore/dataTypes";
import { authSuccess } from "reduxStore/reducers/authReducer";
import AuthGuard from "AuthGuard";
import UserDetailsPage from "pages/Admin/UsersPage/UserDetailsPage";
import AdminTransactionDetailsPage from "pages/Admin/TransactionPage/AdminTransactionDetailsPage";
import { authAdminSuccess } from "reduxStore/reducers/adminAuthReducer";
import WalletDetailsPage from "pages/Admin/WalletPage/WalletDetailsPage";
import SettingsPage from "pages/Admin/SettingsPage/SettingsPage";
import AddUserPage from "pages/Admin/AddUserPage/AddUserPage";

const router = createBrowserRouter([
  {
    path: "/",
    element: <HomeLayout />,
    children: [
      { index: true, path: "/", element: <HomePage /> },
      { path: "/about-us", element: <AboutUsPage /> },
      { path: "/contact-us", element: <ContactUsPage /> },
      { path: "/faq", element: <FAQPage /> },
      { path: "/privacy-policy", element: <PrivacyPolicyPage /> },
      { path: "/terms-condition", element: <TermsConditionPage /> },
    ],
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      { path: "", element: <Navigate to="/auth/login" /> },
      { index: true, path: "login", element: <LoginPage /> },
      { path: "/auth/signup", element: <SignupPage /> },
      { path: "/auth/forget-password", element: <ForgetPassword /> },
      {
        path: "/auth/two-factor-authentication",
        element: <TwoFactorAuthentication />,
      },
      {
        path: "/auth/verify-email",
        element: <VerifyEmailPage />,
      },
      {
        path: "/auth/verify-code",
        element: <VerificationCodePage />,
      },
    ],
  },
  {
    path: "/dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <Navigate to="/dashboard" /> },
      { index: true, element: <DashboardPage /> },
      { path: "/dashboard/wallet", element: <WalletPage /> },
      { path: "/dashboard/transactions", element: <TransactionsPage /> },
      { path: "/dashboard/profile-settings", element: <ProfileSettingsPage /> },
      { path: "/dashboard/help-support", element: <SupportHelpPage /> },
      {
        path: "/dashboard/transactions/:id",
        element: <TransactionDetails />,
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <AuthGuard>
        <AdminLayout />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <Navigate to="/admin/login" /> },
      { index: true, path: "login", element: <AdminLoginPage /> },
      { path: "/admin/dashboard", element: <AdminDashboardPage /> },
      { path: "/admin/wallet", element: <AdminWalletPage /> },
      { path: "/admin/wallet/:id", element: <WalletDetailsPage /> },
      { path: "/admin/transactions", element: <AdminTransactionPage /> },
      {
        path: "/admin/transactions/:id",
        element: <AdminTransactionDetailsPage />,
      },
      { path: "/admin/users", element: <AdminUserPage /> },
      { path: "/admin/user/create", element: <AddUserPage /> },
      { path: "/admin/users/:id", element: <UserDetailsPage /> },
      { path: "/admin/settings", element: <SettingsPage /> },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);

const AppRouter = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const authData = getFromStore<any>(StoreType.AUTH);
    if (authData) {
      if (authData?.role?.toLowerCase() === "user") {
        dispatch(
          authSuccess({
            data: authData,
            responseCode: 112101,
            status: "success",
            message: "Login Successful!",
          })
        );
      } else {
        dispatch(
          authAdminSuccess({
            data: authData,
            responseCode: 112101,
            status: "success",
            message: "Login Successful!",
          })
        );
      }
    }
  }, []);

  return <RouterProvider router={router} />;
};
export default AppRouter;
